// src/pages/TimeBilling.js — where tracked time turns into money.
// Three tabs: Time (everyone's sessions), Invoices (generate/void), Rates.
// Admin/manager only — it exposes every agent's hours and the billing config.
import { useState, useEffect, useCallback } from "react";
import { Receipt, Clock, DollarSign, Plus } from "lucide-react";
import { fmtDur } from "../components/TimeTracker";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (p, o = {}) =>
  fetch(API + p, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...o });

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const STATUS_CLR = { draft: "#94a3b8", sent: "#60a5fa", paid: "#34d399", void: "#64748b" };

export default function TimeBilling() {
  const [tab, setTab] = useState("time");
  const [sessions, setSessions] = useState([]);
  const [totals, setTotals] = useState({ total_seconds: 0, billable_cents: 0 });
  const [invoices, setInvoices] = useState([]);
  const [rates, setRates] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState("");
  const [gen, setGen] = useState({ client_id: "", from: "", to: "" });
  const [newRate, setNewRate] = useState({ scope: "default", rate: "", agent_id: "", client_id: "" });

  const loadTime = useCallback(async () => {
    const d = await apiFetch("/time/sessions").then(r => r.json());
    setSessions(d.sessions || []); setTotals({ total_seconds: d.total_seconds || 0, billable_cents: d.billable_cents || 0 });
  }, []);
  const loadInv = useCallback(async () => setInvoices(await apiFetch("/billing/invoices").then(r => r.json()).catch(() => [])), []);
  const loadRates = useCallback(async () => setRates(await apiFetch("/billing/rates").then(r => r.json()).catch(() => [])), []);

  useEffect(() => { loadTime(); loadInv(); loadRates(); }, [loadTime, loadInv, loadRates]);
  useEffect(() => {
    apiFetch("/clients").then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : (d.clients || []))).catch(() => {});
    apiFetch("/agents").then(r => r.json()).then(d => setAgents(Array.isArray(d) ? d : (d.agents || []))).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy("gen"); setNote(null);
    try {
      const body = {};
      if (gen.client_id) body.client_id = gen.client_id;
      if (gen.from) body.from = gen.from;
      if (gen.to) body.to = gen.to;
      const r = await apiFetch("/billing/invoices", { method: "POST", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setNote({ ok: false, text: d.error }); return; }
      setNote({ ok: true, text: `${d.number} created — ${money(d.total_cents)}` });
      loadInv(); loadTime();
    } finally { setBusy(""); }
  };

  const setStatus = async (inv, status) => {
    if (status === "void" && !window.confirm("Void this invoice? Its time becomes billable again.")) return;
    await apiFetch(`/billing/invoices/${inv.id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    loadInv(); loadTime(); if (detail?.id === inv.id) openInv(inv.id);
  };

  const openInv = async (id) => setDetail(await apiFetch(`/billing/invoices/${id}`).then(r => r.json()));

  const addRate = async () => {
    const cents = Math.round(Number(newRate.rate) * 100);
    if (!cents) return;
    const body = { scope: newRate.scope, rate_cents: cents };
    if (newRate.scope === "agent") body.agent_id = newRate.agent_id;
    if (newRate.scope === "client") body.client_id = newRate.client_id;
    const r = await apiFetch("/billing/rates", { method: "POST", body: JSON.stringify(body) });
    if (!r.ok) { const d = await r.json(); setNote({ ok: false, text: d.error }); return; }
    setNewRate({ scope: "default", rate: "", agent_id: "", client_id: "" }); loadRates();
  };

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 12 };
  const ipt = { background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 10px", color: "var(--t-text-primary)", fontSize: 12.5 };
  const btn = (primary) => ({ background: primary ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-panel)", color: primary ? "#fff" : "#94a3b8", border: primary ? "none" : "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 15px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>Time &amp; Billing</h1>
      <p style={{ margin: "4px 0 18px", color: "var(--t-text-faint)", fontSize: 13 }}>
        {fmtDur(totals.total_seconds)} tracked · {money(totals.billable_cents)} billable · {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
      </p>

      {note && <div style={{ marginBottom: 14, background: note.ok ? "rgba(52,211,153,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${note.ok ? "rgba(52,211,153,0.3)" : "rgba(239,68,68,0.3)"}`, color: note.ok ? "#34d399" : "#f87171", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>{note.text}</div>}

      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[["time", "Time", Clock], ["invoices", "Invoices", Receipt], ["rates", "Rates", DollarSign]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, fontSize: 13, fontWeight: tab === k ? 700 : 500, cursor: "pointer", fontFamily: "inherit",
            color: tab === k ? "#fff" : "var(--t-text-muted)", background: tab === k ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-input)", border: tab === k ? "none" : "1px solid var(--t-border-soft)",
          }}><Icon size={14} />{l}</button>
        ))}
      </div>

      {/* ── Time ── */}
      {tab === "time" && (
        <div style={{ ...panel, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 90px 110px 90px", gap: 8, padding: "10px 14px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <span>When</span><span>Who / What</span><span>Duration</span><span>Amount</span><span>Status</span>
          </div>
          {sessions.map(s => (
            <div key={s.id} style={{ display: "grid", gridTemplateColumns: "150px 1fr 90px 110px 90px", gap: 8, padding: "9px 14px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12.5, alignItems: "center" }}>
              <span style={{ color: "#64748b" }}>{new Date(s.started_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              <span style={{ color: "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <b>{s.agent_name}</b> · {s.entity_type}{s.note ? ` — ${s.note}` : ""}
              </span>
              <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--t-text-body)" }}>{s.ended_at ? fmtDur(s.duration_seconds) : "running"}</span>
              <span style={{ color: s.billable ? "#34d399" : "#64748b", fontWeight: 700 }}>
                {s.billable ? money(Math.round((s.duration_seconds || 0) / 3600 * (s.rate_cents || 0))) : "non-billable"}
              </span>
              <span style={{ fontSize: 11, color: s.invoice_id ? "#34d399" : "#64748b" }}>{s.invoice_id ? "invoiced" : "open"}</span>
            </div>
          ))}
          {!sessions.length && <div style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13 }}>No time tracked yet. Start a timer on a ticket or project.</div>}
        </div>
      )}

      {/* ── Invoices ── */}
      {tab === "invoices" && (
        <>
          <div style={{ ...panel, padding: 14, marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>Client</label>
              <select value={gen.client_id} onChange={e => setGen(g => ({ ...g, client_id: e.target.value }))} style={{ ...ipt, minWidth: 180 }}>
                <option value="">All clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select></div>
            <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>From</label>
              <input type="date" value={gen.from} onChange={e => setGen(g => ({ ...g, from: e.target.value }))} style={ipt} /></div>
            <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>To</label>
              <input type="date" value={gen.to} onChange={e => setGen(g => ({ ...g, to: e.target.value }))} style={ipt} /></div>
            <button onClick={generate} disabled={busy === "gen"} style={btn(true)}>{busy === "gen" ? "Generating…" : "Generate invoice"}</button>
            <span style={{ fontSize: 11.5, color: "#64748b", marginLeft: "auto" }}>Bills all uninvoiced billable time in range, one line per person.</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: detail ? "minmax(0,1fr) 380px" : "1fr", gap: 14, alignItems: "start" }}>
            <div style={{ ...panel, overflow: "hidden" }}>
              {invoices.map(i => (
                <div key={i.id} onClick={() => openInv(i.id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderBottom: "1px solid var(--t-border-soft)", cursor: "pointer", background: detail?.id === i.id ? "var(--t-surface-mid)" : "transparent" }}>
                  <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 800, color: "var(--t-text-primary)", fontSize: 13 }}>{i.number}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.client_name || "—"}</span>
                  <span style={{ fontSize: 11, color: "#64748b" }}>{fmtD(i.created_at)}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#34d399", width: 90, textAlign: "right" }}>{money(i.total_cents)}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: STATUS_CLR[i.status], width: 54, textAlign: "right" }}>{i.status}</span>
                </div>
              ))}
              {!invoices.length && <div style={{ padding: 40, textAlign: "center", color: "#475569", fontSize: 13 }}>No invoices yet.</div>}
            </div>

            {detail && (
              <div style={{ ...panel, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: "var(--t-text-primary)", fontFamily: "ui-monospace, monospace" }}>{detail.number}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{detail.client_name || "No client"} · {fmtD(detail.created_at)}</div>
                  </div>
                  <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: "#475569", fontSize: 19, cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
                {(detail.lines || []).map(l => (
                  <div key={l.id} style={{ display: "flex", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12.5 }}>
                    <span style={{ flex: 1, color: "var(--t-text-body)" }}>{l.description}</span>
                    <span style={{ fontWeight: 700, color: "#34d399" }}>{money(l.amount_cents)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 15, fontWeight: 800, color: "var(--t-text-primary)" }}>
                  <span>Total</span><span>{money(detail.total_cents)}</span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
                  {detail.status !== "void" && ["draft", "sent", "paid"].filter(s => s !== detail.status).map(s => (
                    <button key={s} onClick={() => setStatus(detail, s)} style={btn(false)}>Mark {s}</button>
                  ))}
                  {detail.status !== "void" && <button onClick={() => setStatus(detail, "void")} style={{ ...btn(false), color: "#f87171", borderColor: "rgba(239,68,68,0.3)" }}>Void</button>}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Rates ── */}
      {tab === "rates" && (
        <>
          <div style={{ ...panel, padding: 14, marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>Scope</label>
              <select value={newRate.scope} onChange={e => setNewRate(r => ({ ...r, scope: e.target.value }))} style={ipt}>
                <option value="default">Default (everything)</option><option value="agent">Per agent</option><option value="client">Per client</option>
              </select></div>
            {newRate.scope === "agent" && (
              <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>Agent</label>
                <select value={newRate.agent_id} onChange={e => setNewRate(r => ({ ...r, agent_id: e.target.value }))} style={{ ...ipt, minWidth: 170 }}>
                  <option value="">Pick…</option>{agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                </select></div>
            )}
            {newRate.scope === "client" && (
              <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>Client</label>
                <select value={newRate.client_id} onChange={e => setNewRate(r => ({ ...r, client_id: e.target.value }))} style={{ ...ipt, minWidth: 180 }}>
                  <option value="">Pick…</option>{clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select></div>
            )}
            <div><label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>Hourly rate ($)</label>
              <input type="number" min="0" step="5" value={newRate.rate} onChange={e => setNewRate(r => ({ ...r, rate: e.target.value }))} placeholder="150" style={{ ...ipt, width: 100 }} /></div>
            <button onClick={addRate} style={btn(true)}><Plus size={13} style={{ verticalAlign: -2 }} /> Set rate</button>
          </div>
          <div style={{ ...panel, overflow: "hidden" }}>
            {rates.map(r => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--t-border-soft)", fontSize: 12.5 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", color: "#a78bfa", width: 64 }}>{r.scope}</span>
                <span style={{ flex: 1, color: "var(--t-text-body)" }}>{r.agent_name || r.client_name || r.project_title || "All work"}</span>
                <span style={{ fontWeight: 800, color: "#34d399" }}>{money(r.rate_cents)}/h</span>
                <button onClick={async () => { await apiFetch(`/billing/rates/${r.id}`, { method: "DELETE" }); loadRates(); }}
                  style={{ background: "none", border: "none", color: "#f87171", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
              </div>
            ))}
            {!rates.length && <div style={{ padding: 30, textAlign: "center", color: "#475569", fontSize: 13 }}>No rates set — add a default rate so time can be priced.</div>}
          </div>
        </>
      )}
    </div>
  );
}
